import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type { RevisionDiffResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { FileDiff } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { InlineTurnCard } from './docChatStyles'

const MAX_HEADLINE_EVENTS = 3

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeadlineList = styled.ul`
  margin: 0;
  padding-left: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-start;
`

interface DocChatRevisionDiffCardProps {
  result: RevisionDiffResult
  /** False while the target document row is still hydrating. */
  canOpen: boolean
  onOpen: () => void
}

/** Unknown (null) counts are left out; a measured zero is kept. */
function statusCountParts(result: RevisionDiffResult): string[] {
  const { added, removed, changed } = result.counts.by_status
  const parts: string[] = []
  if (added !== null && added !== undefined) parts.push(t`${added} added`)
  if (removed !== null && removed !== undefined) {
    parts.push(t`${removed} removed`)
  }
  if (changed !== null && changed !== undefined) {
    parts.push(t`${changed} changed`)
  }
  return parts
}

function headlineText({
  headline,
  impact,
}: RevisionDiffResult['headline_events'][number]): string {
  return impact ? t`${headline} (${impact} impact)` : headline
}

function revisionRangeText(result: RevisionDiffResult): string | null {
  const from = result.from_revision ?? null
  const to = result.to_revision ?? null
  if (from !== null && to !== null) return t`Revision ${from} to ${to}`
  if (to !== null) return t`Revision ${to}`
  if (from !== null) return t`Compared with revision ${from}`
  return null
}

export function DocChatRevisionDiffCard({
  result,
  canOpen,
  onOpen,
}: DocChatRevisionDiffCardProps): React.ReactElement {
  useLingui()
  const range = revisionRangeText(result)
  const counts = statusCountParts(result)
  const headlines = result.headline_events.slice(0, MAX_HEADLINE_EVENTS)

  return (
    <InlineTurnCard>
      <CardHeader>
        <FileDiff aria-hidden />
        <Text as='p' variant='label' weight='medium'>
          <Trans>What changed</Trans>
        </Text>
        {range ? (
          <Text as='p' variant='caption' color='muted'>
            {range}
          </Text>
        ) : null}
      </CardHeader>
      {counts.length > 0 ? (
        <Text as='p' variant='caption' color='muted'>
          {counts.join(' · ')}
        </Text>
      ) : null}
      {result.verdict ? (
        <Text as='p' variant='body'>
          {result.verdict}
        </Text>
      ) : null}
      {headlines.length > 0 ? (
        <HeadlineList>
          {headlines.map((event, index) => (
            <li key={`${event.headline}-${index}`}>
              <Text as='span' variant='caption'>
                {headlineText(event)}
              </Text>
            </li>
          ))}
        </HeadlineList>
      ) : null}
      <FooterRow>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={!canOpen}
          onClick={onOpen}>
          <Trans>Open revision</Trans>
        </Button>
      </FooterRow>
    </InlineTurnCard>
  )
}
